import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Package, TrendingUp, Receipt, MapPin, CalendarDays } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApi, eur, dateFr } from "@/lib/api";
import { PageShell, StatusPill, Empty, ListingCard, Pill } from "@/components/common";

export function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid={testid}>
      <div className="flex justify-between items-start"><p className="eyebrow">{label}</p>{Icon && <Icon className="w-4 h-4 text-stone-400" />}</div>
      <p className="font-serif text-4xl text-forest mt-3">{value}</p>
    </div>
  );
}

export function ProSpace() {
  const api = useApi();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/pro/dashboard").then((r) => setD(r.data)); }, [api]);
  if (!d) return <PageShell><p className="text-stone-500">Chargement…</p></PageShell>;
  return (
    <PageShell eyebrow={d.business.company_name || "Professionnel"} title="Espace professionnel" actions={<Link to="/publier" className="rounded-full bg-forest text-white px-5 py-2.5 text-sm font-semibold" data-testid="pro-new-listing">Nouvelle annonce</Link>}>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <Stat label="Annonces" value={d.stats.listings} icon={Package} testid="pro-stat-listings" />
        <Stat label="Actives" value={d.stats.active} icon={Package} testid="pro-stat-active" />
        <Stat label="Vues" value={d.stats.views} icon={Eye} testid="pro-stat-views" />
        <Stat label="Ventes" value={d.stats.sales} icon={TrendingUp} testid="pro-stat-sales" />
        <Stat label="CA objets" value={eur(d.stats.revenue)} icon={TrendingUp} testid="pro-stat-revenue" />
      </div>
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 bg-white rounded-2xl border border-stone-200 p-6">
          <p className="font-semibold text-forest mb-4 flex items-center gap-2"><Receipt className="w-4 h-4" />Factures de commission</p>
          {!d.invoices.length ? <Empty testid="pro-invoices-empty">Aucune facture.</Empty> : (
            <Table data-testid="pro-invoices-table">
              <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Date</TableHead><TableHead>Objet</TableHead><TableHead className="text-right">Commission HT</TableHead><TableHead className="text-right">TVA</TableHead></TableRow></TableHeader>
              <TableBody>{d.invoices.map((i) => <TableRow key={i.number}><TableCell className="font-mono text-xs">{i.number}</TableCell><TableCell>{dateFr(i.date)}</TableCell><TableCell>{i.label}</TableCell><TableCell className="text-right">{eur(i.commission_ht)}</TableCell><TableCell className="text-right">{eur(i.vat)}</TableCell></TableRow>)}</TableBody>
            </Table>
          )}
          <p className="text-xs text-stone-500 mt-4">Abonnement Pro (hypothèse) : {eur(d.subscription_price)}/mois — non facturé dans cette version.</p>
        </div>
        <div className="lg:col-span-5 space-y-3">
          <p className="eyebrow">Commandes</p>
          {!d.orders.length && <Empty testid="pro-orders-empty">Aucune commande.</Empty>}
          {d.orders.map((o) => <div key={o.id} className="bg-white rounded-2xl border border-stone-200 p-4 flex justify-between"><span>{o.listing_title}</span><StatusPill status={o.status} /></div>)}
        </div>
      </div>
      <p className="eyebrow mt-12 mb-4">Catalogue</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{d.listings.map((l) => <ListingCard key={l.id} l={l} />)}</div>
    </PageShell>
  );
}

export function ProviderSpace() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const load = useCallback(() => {
    api.get("/clearance-requests").then((r) => setData(r.data));
    api.get("/bookings").then((r) => setBookings(r.data));
  }, [api]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <PageShell><p className="text-stone-500">Chargement…</p></PageShell>;
  const mine = bookings.filter((b) => b.status !== "cancelled");
  return (
    <PageShell eyebrow="Prestataire validé" title="Espace prestataire">
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-3">
          <p className="eyebrow">Missions disponibles ({data.missions.length})</p>
          {!data.missions.length && <Empty testid="missions-empty">Aucune mission ouverte.</Empty>}
          {data.missions.map((m) => (
            <Link key={m.id} to={`/debarras/${m.id}`} data-testid={`mission-${m.id}`} className="block bg-white rounded-2xl border border-stone-200 p-5 hover:border-forest transition-colors">
              <div className="flex justify-between items-center"><p className="font-medium flex items-center gap-2"><MapPin className="w-4 h-4" />{m.city} ({m.postal_code}) · {m.volume_m3} m³</p>{m.my_quote ? <Pill tone="green">Devis envoyé</Pill> : <StatusPill status={m.status} />}</div>
              <p className="text-xs text-stone-500 mt-1">Étage {m.floor} · {m.elevator ? "ascenseur" : "sans ascenseur"} · {m.qualification === "reuse" ? "réemploi" : m.qualification === "waste" ? "déchets" : "mixte"} · {m.preferred_date}</p>
              <p className="text-sm mt-2 line-clamp-2">{m.items_description}</p>
            </Link>
          ))}
        </div>
        <div className="lg:col-span-5 space-y-3">
          <p className="eyebrow flex items-center gap-2"><CalendarDays className="w-4 h-4" />Planning</p>
          {!mine.length && <Empty testid="planning-empty">Aucun rendez-vous.</Empty>}
          {mine.map((b) => (
            <Link key={b.id} to={`/debarras/${b.request_id}`} data-testid={`planning-${b.id}`} className="block bg-white rounded-2xl border border-stone-200 p-4 hover:border-forest">
              <div className="flex justify-between"><p className="font-medium">{b.date} · {b.city}</p><StatusPill status={b.status} /></div>
              <p className="text-xs text-stone-500">{b.volume_m3} m³ · {eur(b.amount_ttc)} · commission {eur(b.commission)}</p>
            </Link>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
